import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { CandlestickSeries, createChart, createSeriesMarkers } from 'lightweight-charts';
import type { AutoscaleInfo, CandlestickData, IChartApi, IPriceLine, ISeriesApi, PriceLineOptions, SeriesMarker, Time } from 'lightweight-charts';
import { PriceChange } from '@crypto-trading/common/browser';
import { TriggeredMarkerPrimitive, TRIGGER_MARKER_LABEL_SPACE_PX } from './TriggeredMarkerPrimitive';

/** An alert price this far outside its candle's range is from another market. */
const OFF_MARKET_RATIO = 0.3;

const formatClock = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
import {
  CHART_INTERVAL_OPTIONS,
  Interval,
} from '@/hooks/usePositionPriceData';
import { toPublicChartErrorMessage } from '@/lib/live-candles/candle.types';

interface PositionChartProps {
  priceChanges: PriceChange[];
  entryPrice?: number | null;
  exitPrice?: number | null;
  /**
   * Open positions only: a dashed line, shown once price is within one
   * candle-range of it so a far-away liquidation never squashes the candles.
   */
  liquidationPrice?: number | null;
  /**
   * Open positions only: solid TP/SL lines. Updated in place, so changing
   * them never rebuilds the chart or loses the view.
   */
  takeProfitPrice?: number | null;
  stopLossPrice?: number | null;
  /**
   * Trailing stop loss: the price it starts at (before it is active) and the
   * price it would close at now (once active). Same in-place updates.
   */
  trailingStartPrice?: number | null;
  trailingStopPrice?: number | null;
  isProfitable?: boolean;
  openedAt: string | null | undefined;
  closedAt?: string | null;
  markerText?: string; // Text to show on the marker (default: "Opened")
  entryMarkerVariant?: 'arrow' | 'callout';
  onRequestOlderCandles?: () => void | Promise<void>;
  onRequestNewerCandles?: () => void | Promise<void>;
  hasMoreHistory?: boolean;
  hasMoreFuture?: boolean;
  onFollowLiveChange?: (followLive: boolean) => void;
  /**
   * The view the user leaves is remembered per key (e.g. per interval) while
   * the chart is mounted; a key seen for the first time opens at the marker.
   */
  viewportResetKey?: string;
  title?: string;
  headerActions?: React.ReactNode;
  enabledIntervals?: Interval[];
  selectedInterval?: Interval;
  onIntervalChange?: (interval: Interval) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  error?: { message: string } | null;
  onIntervalClick?: () => void;
  emptyMessage?: string;
  /** Without an entry marker, open on the latest this many candles instead of all of them. */
  initialVisibleBars?: number;
}

type ChartCandle = PriceChange;

type ProtectionLineKind = 'takeProfit' | 'stopLoss' | 'trailingStart' | 'trailingStop';

const PROTECTION_LINES: Record<
  ProtectionLineKind,
  { color: string; title: string; dataKey: string; lineStyle: number }
> = {
  takeProfit: { color: '#22c55e', title: 'TP', dataKey: 'takeProfitLine', lineStyle: 0 },
  stopLoss: { color: '#ef4444', title: 'SL', dataKey: 'stopLossLine', lineStyle: 0 },
  // Trailing stop loss: where it starts, then where it would close now.
  trailingStart: { color: '#a78bfa', title: 'Trail starts', dataKey: 'trailingStartLine', lineStyle: 2 },
  trailingStop: { color: '#a78bfa', title: 'Trailing SL', dataKey: 'trailingStopLine', lineStyle: 0 },
};
const NO_PROTECTION_LINES: Record<ProtectionLineKind, null> = {
  takeProfit: null,
  stopLoss: null,
  trailingStart: null,
  trailingStop: null,
};

const validPrice = (price: number | null | undefined): number | null =>
  typeof price === 'number' && Number.isFinite(price) && price > 0 ? price : null;

/** Widens an autoscale range to include `price` when it is within one range of it. */
function includeNearbyPrice(
  range: { minValue: number; maxValue: number },
  price: number | null,
): { minValue: number; maxValue: number } {
  if (price === null) return range;
  const span = range.maxValue - range.minValue;
  if (price < range.minValue - span || price > range.maxValue + span) return range;
  return {
    minValue: Math.min(range.minValue, price),
    maxValue: Math.max(range.maxValue, price),
  };
}

const CHART_HEIGHT = 300;

function toCandlestick(candle: ChartCandle): CandlestickData {
  return {
    time: (new Date(candle.startTime).getTime() / 1000) as Time,
    open: Number.parseFloat(candle.openPrice),
    high: Number.parseFloat(candle.highPrice),
    low: Number.parseFloat(candle.lowPrice),
    close: Number.parseFloat(candle.closePrice),
  };
}

function sameStart(a: ChartCandle | undefined, b: ChartCandle | undefined): boolean {
  if (!a || !b) return false;
  return new Date(a.startTime).getTime() === new Date(b.startTime).getTime();
}

function sameCandle(a: ChartCandle, b: ChartCandle): boolean {
  return (
    sameStart(a, b) &&
    a.openPrice === b.openPrice &&
    a.highPrice === b.highPrice &&
    a.lowPrice === b.lowPrice &&
    a.closePrice === b.closePrice
  );
}

/**
 * True when `next` only changes the latest candles of `previous` or appends
 * a few new ones (live updates), so the series can be updated in place
 * instead of rebuilding the chart.
 */
function isLiveUpdate(previous: ChartCandle[], next: ChartCandle[]): boolean {
  if (previous.length === 0) return false;
  if (next.length < previous.length || next.length > previous.length + 2) return false;
  const lastIndex = previous.length - 1;
  return sameStart(previous[0], next[0]) && sameStart(previous[lastIndex], next[lastIndex]);
}

export const PositionChart: React.FC<PositionChartProps> = ({
  priceChanges,
  entryPrice,
  exitPrice,
  liquidationPrice,
  takeProfitPrice,
  stopLossPrice,
  trailingStartPrice,
  trailingStopPrice,
  isProfitable = false,
  openedAt,
  closedAt,
  markerText = 'Opened',
  entryMarkerVariant = 'arrow',
  onRequestOlderCandles,
  onRequestNewerCandles,
  hasMoreHistory = true,
  hasMoreFuture = true,
  onFollowLiveChange,
  viewportResetKey,
  title = 'Price Chart',
  headerActions,
  enabledIntervals = [],
  selectedInterval,
  onIntervalChange,
  isLoading = false,
  isLoadingMore = false,
  error = null,
  onIntervalClick,
  emptyMessage,
  initialVisibleBars,
}) => {

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // TP/SL lines live outside the rebuild effect: the latest prices are read
  // from a ref when the series is built and applied in place afterwards.
  const protectionPricesRef = useRef<Record<ProtectionLineKind, number | null>>({
    ...NO_PROTECTION_LINES,
  });
  protectionPricesRef.current = {
    takeProfit: validPrice(takeProfitPrice),
    stopLoss: validPrice(stopLossPrice),
    trailingStart: validPrice(trailingStartPrice),
    trailingStop: validPrice(trailingStopPrice),
  };
  const protectionLinesRef = useRef<Record<ProtectionLineKind, IPriceLine | null>>({
    ...NO_PROTECTION_LINES,
  });
  /** Creates, moves or removes the TP/SL lines to match the latest prices. */
  const syncProtectionLines = (series: ISeriesApi<'Candlestick'> | null) => {
    if (!series) return;
    const container = chartContainerRef.current;
    (Object.keys(PROTECTION_LINES) as ProtectionLineKind[]).forEach((kind) => {
      const price = protectionPricesRef.current[kind];
      const line = protectionLinesRef.current[kind];
      const { color, title, dataKey, lineStyle } = PROTECTION_LINES[kind];
      if (price === null) {
        if (line) series.removePriceLine(line);
        protectionLinesRef.current[kind] = null;
        if (container) delete container.dataset[dataKey];
        return;
      }
      if (line) {
        line.applyOptions({ price });
      } else {
        protectionLinesRef.current[kind] = series.createPriceLine({
          price,
          color,
          lineWidth: 1,
          lineStyle,
          axisLabelVisible: true,
          title,
          lineVisible: true,
          axisLabelColor: color,
          axisLabelTextColor: 'white',
        });
      }
      if (container) container.dataset[dataKey] = String(price);
    });
  };
  const lastLeftRequestAtRef = useRef<number>(0);
  const olderRequestInFlightRef = useRef(false);
  // The view the user left, per viewportResetKey. Each chart instance writes
  // under the key it was built for (its teardown runs after a key change),
  // so going back to an interval restores that interval's view.
  const savedViewsRef = useRef(new Map<string | undefined, { from: Time; to: Time }>());
  // Paging callbacks and flags change while the user scrolls; the chart reads
  // them from refs so a change never rebuilds it mid-gesture.
  const pagingRef = useRef({
    onRequestOlderCandles,
    onRequestNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    onFollowLiveChange,
  });
  /** Re-checks paging and live-follow for the current view (set while a chart exists). */
  const rangeHandlerRef = useRef<(() => void) | null>(null);
  pagingRef.current = {
    onRequestOlderCandles,
    onRequestNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    onFollowLiveChange,
  };
  const priceChangesRef = useRef(priceChanges);
  priceChangesRef.current = priceChanges;
  /** Candles the chart currently draws. */
  const shownCandlesRef = useRef<ChartCandle[] | null>(null);
  const candleCountRef = useRef(0);
  // Live candle updates are applied to the series; anything else rebuilds the chart.
  const dataVersionRef = useRef({ data: priceChanges, version: 0 });
  if (dataVersionRef.current.data !== priceChanges) {
    if (!isLiveUpdate(dataVersionRef.current.data, priceChanges)) {
      dataVersionRef.current.version += 1;
    }
    dataVersionRef.current.data = priceChanges;
  }
  const dataVersion = dataVersionRef.current.version;
  const showChart = !isLoading && !error && priceChanges.length > 0;
  // Only a closed position's exit line is colored by profit; a live PnL
  // crossing zero must not rebuild an open position's chart.
  const exitLineColor = exitPrice ? (isProfitable ? '#4ade80' : '#f87171') : null;

  useEffect(() => {
    const priceChanges = priceChangesRef.current;
    if (!showChart || !chartContainerRef.current || priceChanges.length === 0) return;
    const viewKey = viewportResetKey;

    // Create chart
    const hasEntryPrice =
      typeof entryPrice === 'number' && Number.isFinite(entryPrice) && entryPrice > 0;
    const hasLiquidationPrice =
      typeof liquidationPrice === 'number' &&
      Number.isFinite(liquidationPrice) &&
      liquidationPrice > 0;
    const hasCalloutMarker =
      entryMarkerVariant === 'callout' && Boolean(openedAt) && hasEntryPrice;
    // autoSize follows the container (ResizeObserver), so a moment at zero
    // width (drawer animating, Telegram viewport change) never sticks.
    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      layout: {
        background: { color: '#1a1b20' },
        textColor: '#717680',
      },
      grid: {
        vertLines: { color: '#2e2f35' },
        horzLines: { color: '#2e2f35' },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: 'rgba(112, 132, 153, 0.2)',
      },
      rightPriceScale: {
        borderColor: 'rgba(112, 132, 153, 0.2)',
        visible: true,
        scaleMargins: {
          top: 0.05,
          bottom: 0.05,
        },
        entireTextOnly: false,
        autoScale: true,
        ticksVisible: true,
        invertScale: false,
      },
      leftPriceScale: {
        visible: false, // Hide left price scale to ensure right one is used
      },
      crosshair: {
        vertLine: {
          color: '#2e2f35',
          width: 2,
          style: 1,
          labelBackgroundColor: '#6ab2f2',
        },
        horzLine: {
          color: '#2e2f35',
          width: 2,
          style: 1,
          labelBackgroundColor: '#6ab2f2',
        },
      },
    });

    chartRef.current = chart;

    // Create candlestick series - explicitly attach to right price scale
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#17b26a',
      downColor: '#d92d20',
      borderUpColor: '#17b26a',
      borderDownColor: '#d92d20',
      wickUpColor: '#17b26a',
      wickDownColor: '#d92d20',
      priceLineVisible: false,
      lastValueVisible: false,
      priceScaleId: 'right', // Explicitly attach to right price scale
      // Liquidation line: pull it into view only when it is within one
      // candle-range of the candles, so far-away liquidation never squashes
      // the candles; otherwise it stays off-screen until price gets closer.
      // TP/SL lines follow the same rule, read from a ref so an edit never
      // rebuilds the chart.
      autoscaleInfoProvider: (original: () => AutoscaleInfo | null) => {
        const info = original();
        if (!info || !info.priceRange) return info;
        const candles = info.priceRange;
        // Each line is measured against the candles alone. The entry and
        // exit lines are always kept in view (price lines are not part of
        // autoscale, so an entry just past the candles would be cut off);
        // the alert chart's callout marker pins itself to the pane edge.
        const always = [
          hasEntryPrice && entryMarkerVariant !== 'callout' ? entryPrice : null,
          exitPrice || null,
        ].filter((price): price is number => price != null);
        const priceRange = [
          hasLiquidationPrice ? liquidationPrice : null,
          protectionPricesRef.current.takeProfit,
          protectionPricesRef.current.stopLoss,
          protectionPricesRef.current.trailingStart,
          protectionPricesRef.current.trailingStop,
        ].reduce(
          (range, price) => {
            const widened = includeNearbyPrice(candles, price);
            return {
              minValue: Math.min(range.minValue, widened.minValue),
              maxValue: Math.max(range.maxValue, widened.maxValue),
            };
          },
          {
            minValue: Math.min(candles.minValue, ...always),
            maxValue: Math.max(candles.maxValue, ...always),
          },
        );
        if (priceRange.minValue === candles.minValue && priceRange.maxValue === candles.maxValue) {
          return info;
        }
        return { ...info, priceRange };
      },
    });

    candlestickSeriesRef.current = candlestickSeries;

    // Convert price changes to candlestick data
    const candleData: CandlestickData[] = priceChanges
      .map(toCandlestick)
      .sort((a, b) => (a.time as number) - (b.time as number)); // Sort by time in ascending order

    candlestickSeries.setData(candleData);
    shownCandlesRef.current = priceChanges;
    candleCountRef.current = candleData.length;

    // Calculate price dynamics to adapt scale/formatting
    const prices = candleData.flatMap((candle) => [candle.open, candle.high, candle.low, candle.close]);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const avgPrice = (minPrice + maxPrice) / 2 || 1;
    const rangePercentage = avgPrice === 0 ? 0 : (priceRange / avgPrice) * 100;

    // Adaptive precision for small-price assets
    let precision = 2;
    if (avgPrice < 1 || rangePercentage < 2) {
      precision = 4;
    }
    if (avgPrice < 0.1 || rangePercentage < 0.5) {
      precision = 6;
    }
    const minMove = 1 / Math.pow(10, precision);

    candlestickSeries.applyOptions({
      priceFormat: {
        type: 'price',
        precision,
        minMove,
      },
    });

    // Adaptive scale margins to add breathing room for tight ranges
    let scaleMargins = { top: 0.05, bottom: 0.05 };
    if (rangePercentage < 0.5) {
      scaleMargins = { top: 0.18, bottom: 0.18 };
    } else if (rangePercentage < 1) {
      scaleMargins = { top: 0.12, bottom: 0.12 };
    } else if (rangePercentage < 2) {
      scaleMargins = { top: 0.08, bottom: 0.08 };
    }

    if (hasCalloutMarker) {
      const labelSpaceFraction = TRIGGER_MARKER_LABEL_SPACE_PX / CHART_HEIGHT;
      scaleMargins = {
        ...scaleMargins,
        bottom: Math.max(scaleMargins.bottom, labelSpaceFraction),
      };
    }

    // Ensure price scale is visible and shows labels
    // This is especially important for opened positions without exit price
    const priceScale = chart.priceScale('right');
    priceScale.applyOptions({
      visible: true,
      entireTextOnly: false,
      autoScale: true,
      ticksVisible: true,
      invertScale: false,
      scaleMargins,
    });

    // Helper function to find the correct candle for a marker
    // Checks if the price is within the candle's range, otherwise checks left neighbor
    // Works with sorted candleData array
    const findCandleForMarker = (
      timestamp: number,
      targetPrice?: number,
    ): CandlestickData | null => {
      if (candleData.length === 0) return null;
      
      const targetTimeSeconds = timestamp / 1000; // Convert to seconds
      
      // Find the candle whose time is closest to but not after the target timestamp
      // Since candleData is sorted by time, we can search directly
      let candleIndex = -1;
      let closestTimeDiff = Infinity;
      
      for (let i = 0; i < candleData.length; i++) {
        const candleTime = candleData[i].time as number;
        const timeDiff = targetTimeSeconds - candleTime;
        
        // Find the candle that starts before or at the target time, and is closest
        if (timeDiff >= 0 && timeDiff < closestTimeDiff) {
          closestTimeDiff = timeDiff;
          candleIndex = i;
        }
      }
      
      // If no candle found before the target time, use the first candle
      if (candleIndex === -1) {
        candleIndex = 0;
      }
      
      if (candleIndex < 0 || candleIndex >= candleData.length) {
        return null;
      }
      
      const candle = candleData[candleIndex];

      if (targetPrice == null || !Number.isFinite(targetPrice)) {
        return candle;
      }
      
      // Check if the target price is within this candle's price range
      if (targetPrice >= candle.low && targetPrice <= candle.high) {
        return candle;
      }
      
      // If not, check the left neighbor (previous candle)
      if (candleIndex > 0) {
        const leftCandle = candleData[candleIndex - 1];
        if (targetPrice >= leftCandle.low && targetPrice <= leftCandle.high) {
          return leftCandle;
        }
      }
      
      // If neither candle contains the price, return the original candle
      return candle;
    };

    // Add price lines for entry and exit prices
    // Callout = trigger dot only; skip entry line to avoid a second horizontal + axis label
    if (hasEntryPrice && entryMarkerVariant !== 'callout') {
      const entryPriceLine: PriceLineOptions = {
        price: entryPrice,
        color: '#6ab2f2',
        lineWidth: 2,
        lineStyle: 0, // Solid line
        axisLabelVisible: true,
        title: ``,
        lineVisible: true,
        axisLabelColor: '#6ab2f2',
        axisLabelTextColor: 'white',
      };
      candlestickSeries.createPriceLine(entryPriceLine);
    }

    if (hasLiquidationPrice) {
      candlestickSeries.createPriceLine({
        price: liquidationPrice,
        color: '#f59e0b',
        lineWidth: 1,
        lineStyle: 2, // Dashed line
        axisLabelVisible: true,
        title: 'Liq.',
        lineVisible: true,
        axisLabelColor: '#f59e0b',
        axisLabelTextColor: 'black',
      });
    }

    syncProtectionLines(candlestickSeries);

    if (exitPrice && exitLineColor) {
      const exitColor = exitLineColor;
      const exitPriceLine: PriceLineOptions = {
        price: exitPrice,
        color: exitColor,
        lineWidth: 2,
        lineStyle: 0, // Solid line
        axisLabelVisible: true,
        title: ``,
        lineVisible: true,
        axisLabelColor: exitColor,
        axisLabelTextColor: 'white',
      };
      candlestickSeries.createPriceLine(exitPriceLine);
    }

    // Add markers for position opened/closed.
    const markers: SeriesMarker<Time>[] = [];
    let entryCandleForViewport: CandlestickData | null = null;
    let exitCandleForViewport: CandlestickData | null = null;

    if (openedAt && candleData.length > 0) {
      const openedAtTimestamp = new Date(openedAt).getTime();
      const entryCandle = findCandleForMarker(
        openedAtTimestamp,
        hasEntryPrice ? entryPrice : undefined,
      );
      
      if (entryCandle) {
        entryCandleForViewport = entryCandle;
        if (hasEntryPrice) {
          if (entryMarkerVariant === 'callout') {
            // An alert price far outside the candle it fired in came from
            // another market (e.g. a settled contract that kept a stale mark
            // price). Pin the marker to this chart's price at alert time
            // instead of a point off the chart.
            const offMarket =
              entryPrice < entryCandle.low * (1 - OFF_MARKET_RATIO) ||
              entryPrice > entryCandle.high * (1 + OFF_MARKET_RATIO);
            const atPrice = candlestickSeries.priceFormatter().format(entryPrice);
            candlestickSeries.attachPrimitive(
              new TriggeredMarkerPrimitive({
                time: entryCandle.time,
                price: offMarket ? entryCandle.close : entryPrice,
                labelText: offMarket
                  ? `Triggered at ${formatClock(openedAtTimestamp)}`
                  : `Triggered at ${atPrice}`,
              }),
            );
          } else {
            markers.push({
              time: entryCandle.time,
              position: 'belowBar' as const,
              color: 'white',
              shape: 'arrowUp' as const,
              text: markerText,
            });
          }
        }
      }
    }

    if (exitPrice && closedAt && candleData.length > 0) {
      const closedAtTimestamp = new Date(closedAt).getTime();
      const exitCandle = findCandleForMarker(closedAtTimestamp, exitPrice);
      
      if (exitCandle) {
        exitCandleForViewport = exitCandle;
        markers.push({
          time: exitCandle.time,
          position: 'aboveBar' as const,
          color: 'white',
          shape: 'arrowDown' as const,
          text: `Closed`,
        });
      }
    }

    if (markers.length > 0) {
        createSeriesMarkers(candlestickSeries, markers);
    }

    const savedTimeRange = savedViewsRef.current.get(viewKey) ?? null;
    const applyViewport = () => {
      if (
        savedTimeRange &&
        (savedTimeRange.from as number) <= (savedTimeRange.to as number)
      ) {
        try {
          chart.timeScale().setVisibleRange(savedTimeRange);
          return;
        } catch {
          // Saved times are outside this series. Fall back to the alert window.
        }
      }

      const entryIndex = entryCandleForViewport
        ? candleData.findIndex((candle) => candle.time === entryCandleForViewport.time)
        : -1;
      const exitIndex = exitCandleForViewport
        ? candleData.findIndex((candle) => candle.time === exitCandleForViewport.time)
        : -1;
      const edgePadding = 5;

      if (entryIndex >= 0 && exitIndex >= 0 && exitIndex >= entryIndex) {
        chart.timeScale().setVisibleLogicalRange({
          from: Math.max(0, entryIndex - edgePadding),
          to: Math.min(candleData.length - 1, exitIndex + edgePadding),
        });
      } else if (entryIndex >= 0) {
        const visibleBars = Math.min(48, candleData.length);
        const barsBeforeEntry = Math.floor(visibleBars * 0.35);
        const from = Math.max(0, entryIndex - barsBeforeEntry);
        const to = Math.min(candleData.length - 1, from + visibleBars - 1);
        chart.timeScale().setVisibleLogicalRange({ from, to });
      } else if (initialVisibleBars && candleData.length > initialVisibleBars) {
        const last = candleData.length - 1;
        chart.timeScale().setVisibleLogicalRange({
          from: last - initialVisibleBars + 1,
          to: last + 2,
        });
      } else {
        chart.timeScale().fitContent();
      }
    };

    applyViewport();

    let disposed = false;
    // Width can still be 0 on the first frame. Re-apply after layout so bar
    // spacing is computed from the real chart width.
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (disposed) return;
        try {
          applyViewport();
          chart.priceScale('right').applyOptions({
            visible: true,
            autoScale: true,
            ticksVisible: true,
            entireTextOnly: false,
            invertScale: false,
            scaleMargins,
          });
        } catch {
          // Chart was removed before layout finished.
        }
      }, 50);
    });

    const handleLogicalRangeChange = (range: { from: number; to: number } | null) => {
      if (!range) return;
      const visibleTimeRange = chart.timeScale().getVisibleRange();
      if (
        visibleTimeRange &&
        (visibleTimeRange.from as number) <= (visibleTimeRange.to as number)
      ) {
        savedViewsRef.current.set(viewKey, visibleTimeRange);
      }
      const {
        onRequestOlderCandles,
        onRequestNewerCandles,
        hasMoreHistory,
        hasMoreFuture,
        onFollowLiveChange,
      } = pagingRef.current;
      const totalCandles = candleCountRef.current;
      const roundedFrom = Math.floor(range.from);
      const nearLeftEdge = roundedFrom <= 12;
      const nearRightEdge = range.to >= totalCandles - 3;
      const canPaginateFuture = nearRightEdge && hasMoreFuture && Boolean(onRequestNewerCandles);
      onFollowLiveChange?.(nearRightEdge && !canPaginateFuture);

      if (!nearLeftEdge || !hasMoreHistory || !onRequestOlderCandles) {
        // continue to right-edge handling
      } else {
        const now = Date.now();
        if (!olderRequestInFlightRef.current && now - lastLeftRequestAtRef.current >= 800) {
          lastLeftRequestAtRef.current = now;
          const request = onRequestOlderCandles();
          if (request && typeof (request as Promise<void>).finally === 'function') {
            olderRequestInFlightRef.current = true;
            (request as Promise<void>).finally(() => {
              olderRequestInFlightRef.current = false;
            });
          }
        }
      }

      if (!nearRightEdge || !hasMoreFuture || !onRequestNewerCandles) {
        return;
      }
      const now = Date.now();
      if (olderRequestInFlightRef.current || now - lastLeftRequestAtRef.current < 800) {
        return;
      }
      lastLeftRequestAtRef.current = now;
      const request = onRequestNewerCandles();
      if (request && typeof (request as Promise<void>).finally === 'function') {
        olderRequestInFlightRef.current = true;
        (request as Promise<void>).finally(() => {
          olderRequestInFlightRef.current = false;
        });
      }
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleLogicalRangeChange);
    rangeHandlerRef.current = () =>
      handleLogicalRangeChange(chart.timeScale().getVisibleLogicalRange());

    return () => {
      rangeHandlerRef.current = null;
      disposed = true;
      const visibleTimeRange = chart.timeScale().getVisibleRange();
      if (
        visibleTimeRange &&
        (visibleTimeRange.from as number) <= (visibleTimeRange.to as number)
      ) {
        savedViewsRef.current.set(viewKey, visibleTimeRange);
      }
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleLogicalRangeChange);
      chart.remove();
      chartRef.current = null;
      candlestickSeriesRef.current = null;
      protectionLinesRef.current = { ...NO_PROTECTION_LINES };
      shownCandlesRef.current = null;
    };
  }, [
    dataVersion,
    showChart,
    entryPrice,
    exitPrice,
    liquidationPrice,
    exitLineColor,
    openedAt,
    closedAt,
    markerText,
    entryMarkerVariant,
    viewportResetKey,
    initialVisibleBars,
  ]);

  // TP/SL edits move the lines in place (no rebuild, the view stays).
  useEffect(() => {
    syncProtectionLines(candlestickSeriesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeProfitPrice, stopLossPrice, trailingStartPrice, trailingStopPrice]);

  // A finished page (no more history or future) changes what the current
  // view should do, e.g. start following live candles.
  useEffect(() => {
    rangeHandlerRef.current?.();
  }, [hasMoreHistory, hasMoreFuture]);

  // Live updates: redraw only the candles that changed.
  useEffect(() => {
    const series = candlestickSeriesRef.current;
    const shown = shownCandlesRef.current;
    if (!series || !shown || shown === priceChanges) return;
    shownCandlesRef.current = priceChanges;
    candleCountRef.current = priceChanges.length;
    try {
      for (let index = Math.max(0, shown.length - 2); index < priceChanges.length; index += 1) {
        const candle = priceChanges[index];
        const isNewOrLatest = index >= shown.length - 1;
        if (isNewOrLatest) {
          series.update(toCandlestick(candle));
        } else if (!sameCandle(shown[index], candle)) {
          series.update(toCandlestick(candle), true);
        }
      }
    } catch {
      series.setData(priceChanges.map(toCandlestick));
    }
  }, [priceChanges]);

  const visibleIntervals = CHART_INTERVAL_OPTIONS.filter((interval) =>
    enabledIntervals.includes(interval),
  );
  const showIntervalSelector =
    Boolean(onIntervalChange) && visibleIntervals.length > 0 && selectedInterval;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-text-primary">{title}</h3>
        <div className="flex items-center gap-2">
          {showIntervalSelector && (
            <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] backdrop-blur-[4px] [scrollbar-width:none]">
              {visibleIntervals.map((interval) => {
                const isSelected = selectedInterval === interval;
                return (
                  <button
                    key={interval}
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onIntervalClick?.();
                      onIntervalChange?.(interval);
                    }}
                    aria-pressed={isSelected}
                    className={
                      'shrink-0 border px-2.5 py-1.5 text-sm font-medium leading-5 transition-colors ' +
                      (isSelected
                        ? 'edge-glow rounded-xl border-[#00c27e] bg-[#0a0a0a] text-white shadow-sm'
                        : 'rounded-md border-transparent text-[#737373] hover:text-[#d4d4d4]')
                    }
                  >
                    {interval}
                  </button>
                );
              })}
            </div>
          )}
          {headerActions}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
          <Loader2 className="w-6 h-6 animate-spin text-accent" />
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg px-4">
          <p className="text-text-secondary text-sm text-center">
            {toPublicChartErrorMessage(error.message)}
          </p>
        </div>
      )}

      {!isLoading && !error && priceChanges.length === 0 && emptyMessage && (
        <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
          <p className="text-text-secondary text-sm">{emptyMessage}</p>
        </div>
      )}

      {!isLoading && !error && priceChanges.length > 0 && (
        <div className="w-full rounded-xl bg-primary/50">
          <div
            ref={chartContainerRef}
            className="w-full rounded-xl"
            style={{ height: CHART_HEIGHT, overflow: 'visible' }}
          />
        </div>
      )}

      {isLoadingMore && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin text-accent" />
        </div>
      )}
    </div>
  );
};

